import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Heart, Phone, Calendar, DollarSign, ArrowLeft, Plus, Minus, Calculator, MessageSquare, Share2, X, ChevronLeft, ChevronRight } from 'lucide-react';
import { useTranslation } from '../i18n';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Badge } from '../components/ui/badge';
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from '../components/ui/carousel';
import { useAnalytics } from '../hooks/useAnalytics';
import { useCompare } from '../hooks/useCompare';
import SEOHead from '../components/SEOHead';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const VehicleDetailPage = ({ lang, user }) => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation(lang);
  
  // Check if slug is a UUID (old format) or SEO-friendly slug (new format)
  const isUUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(slug);
  
  // Generate SEO-friendly slug from vehicle data (brand-model-year)
  const generateVehicleSlug = (v) => {
    if (!v) return '';
    const brand = (v.brand || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    const model = (v.model || '').toLowerCase().replace(/[^a-z0-9]/g, '').replace(new RegExp(`^${brand}`, 'i'), '');
    const year = v.year || '';
    return `${brand}-${model}-${year}`;
  };
  
  useAnalytics(`vehicle-detail-${slug}`, { eventType: 'vehicle_view', vehicleId: slug });
  const { has: hasInCompare, add: addCompare, remove: removeCompare, max: maxCompare } = useCompare();
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isFavorite, setIsFavorite] = useState(false);
  const [showMessageDialog, setShowMessageDialog] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [similarVehicles, setSimilarVehicles] = useState([]);
  
  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  
  // Reviews
  const [reviews, setReviews] = useState([]);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewForm, setReviewForm] = useState({
    customer_name: '',
    customer_email: '',
    rating: 5,
    comment: ''
  });

  // Quote form
  const [quoteForm, setQuoteForm] = useState({
    name: '',
    email: '',
    phone: '',
    offered_price: '',
    message: ''
  });
  
  // Finance Calculator
  const [financeCalc, setFinanceCalc] = useState({
    price: 0,
    downPayment: 0,
    interestRate: 5.5,
    loanTerm: 60
  });
  const [monthlyPayment, setMonthlyPayment] = useState(0);

  useEffect(() => {
    fetchVehicle();
  }, [slug]);

  // When vehicle loads, fetch favorites and reviews
  useEffect(() => {
    if (vehicle) {
      fetchSimilarVehicles();
      checkFavorite();
      fetchReviews();
    }
  }, [vehicle, user]);

  const fetchSimilarVehicles = async () => {
    if (!vehicle) return;
    try {
      // Get vehicles with similar brand, price range or year
      const response = await axios.get(`${API}/vehicles`, {
        params: {
          limit: 6
        }
      });
      
      // Filter similar vehicles based on criteria
      const similar = response.data
        .filter(v => v.id !== vehicle.id && v.status === 'available')
        .map(v => ({
          ...v,
          score: calculateSimilarityScore(vehicle, v)
        }))
        .sort((a, b) => b.score - a.score)
        .slice(0, 4);
      
      setSimilarVehicles(similar);
    } catch (error) {
      console.error('Error fetching similar vehicles:', error);
    }
  };

  // Similarity algorithm
  const calculateSimilarityScore = (original, candidate) => {
    let score = 0;
    
    // Same brand = +30 points
    if (original.brand === candidate.brand) score += 30;
    
    // Same model = +20 points
    if (original.model === candidate.model) score += 20;
    
    // Year within 2 years = +15 points
    if (Math.abs(original.year - candidate.year) <= 2) score += 15;
    
    // Price within 20% = +20 points
    const priceDiff = Math.abs(original.price - candidate.price) / original.price;
    if (priceDiff <= 0.2) score += 20;
    else if (priceDiff <= 0.4) score += 10;
    
    // Same transmission = +10 points
    if (original.transmission === candidate.transmission) score += 10;
    
    // Same fuel type = +5 points
    if (original.fuel_type === candidate.fuel_type) score += 5;
    
    return score;
  };

  // Lightbox handlers
  const openLightbox = (index) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    document.body.style.overflow = 'auto';
  };

  const goToPrevImage = () => {
    if (vehicle?.images) {
      setLightboxIndex((prev) => (prev === 0 ? vehicle.images.length - 1 : prev - 1));
    }
  };

  const goToNextImage = () => {
    if (vehicle?.images) {
      setLightboxIndex((prev) => (prev === vehicle.images.length - 1 ? 0 : prev + 1));
    }
  };

  // Keyboard navigation for lightbox
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!lightboxOpen) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowLeft') goToPrevImage();
      if (e.key === 'ArrowRight') goToNextImage();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, vehicle]);

  const fetchReviews = async () => {
    if (!vehicle?.id) return;
    try {
      const response = await axios.get(`${API}/reviews/vehicle/${vehicle.id}`);
      setReviews(response.data);
    } catch (error) {
      console.error('Error fetching reviews:', error);
    }
  };

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    if (!vehicle?.id) return;
    try {
      await axios.post(`${API}/reviews`, {
        vehicle_id: vehicle.id,
        review_type: 'vehicle',
        ...reviewForm
      });
      toast.success(t('reviews.reviewSubmitted'));
      setShowReviewForm(false);
      setReviewForm({ customer_name: '', customer_email: '', rating: 5, comment: '' });
      fetchReviews();
    } catch (error) {
      console.error('Error submitting review:', error);
      toast.error(t('reviews.reviewFailed'));
    }
  };

  useEffect(() => {
    if (vehicle) {
      setFinanceCalc(prev => ({ ...prev, price: vehicle.price }));
    }
  }, [vehicle]);

  const fetchVehicle = async () => {
    try {
      const response = await axios.get(`${API}/vehicles/by-slug/${slug}`);
      const vehicleData = response.data;
      setVehicle(vehicleData);
      
      // 301-style redirect: If user came with UUID, redirect to SEO-friendly URL
      if (isUUID && vehicleData) {
        const seoSlug = generateVehicleSlug(vehicleData);
        if (seoSlug) {
          // Replace URL without adding to history (like 301 redirect)
          navigate(`/${seoSlug}`, { replace: true });
        }
      }
    } catch (error) {
      console.error('Error fetching vehicle:', error);
      // Check if vehicle exists but is sold
      try {
        const allVehicles = await axios.get(`${API}/vehicles/all-by-slug/${slug}`);
        if (allVehicles.data && allVehicles.data.status === 'sold') {
          const vehicleData = allVehicles.data;
          setVehicle({ ...vehicleData, isSold: true });
          
          // Also redirect sold vehicle pages to SEO-friendly URL
          if (isUUID && vehicleData) {
            const seoSlug = generateVehicleSlug(vehicleData);
            if (seoSlug) {
              navigate(`/${seoSlug}`, { replace: true });
            }
          }
        } else {
          toast.error(t('common.vehicleNotFound'));
        }
      } catch {
        toast.error(t('common.vehicleNotFound'));
      }
    } finally {
      setLoading(false);
    }
  };

  const checkFavorite = async () => {
    if (!vehicle?.id) return;
    try {
      const response = await axios.get(`${API}/favorites`);
      const isFav = response.data.some(v => v.id === vehicle.id);
      setIsFavorite(isFav);
    } catch (error) {
      console.error('Error checking favorites:', error);
    }
  };

  const toggleFavorite = async () => {
    if (!user) {
      toast.error(t('vehicleDetail.loginToFavorite'));
      return;
    }
    if (!vehicle?.id) return;

    try {
      if (isFavorite) {
        await axios.delete(`${API}/favorites/${vehicle.id}`);
        setIsFavorite(false);
        toast.success(t('vehicleDetail.removedFromFavorites'));
      } else {
        await axios.post(`${API}/favorites?vehicle_id=${vehicle.id}`);
        setIsFavorite(true);
        toast.success(t('vehicleDetail.addedToFavorites'));
      }
    } catch (error) {
      console.error('Error toggling favorite:', error);
      toast.error(t('vehicleDetail.failedToUpdate'));
    }
  };

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    if (!vehicle?.id) return;
    try {
      await axios.post(`${API}/price-quotes`, {
        vehicle_id: vehicle.id,
        ...quoteForm
      });
      toast.success(t('quote.success'));
      setQuoteForm({ name: '', email: '', phone: '', offered_price: '', message: '' });
    } catch (error) {
      console.error('Error submitting quote:', error);
      toast.error(t('quote.error'));
    }
  };

  const handleSendMessage = async () => {
    if (!user) {
      toast.info('Mesaj göndermek için lütfen giriş yapın');
      navigate('/login');
      return;
    }

    if (!messageText.trim()) {
      toast.error(t('messages.pleaseWriteMessage'));
      return;
    }

    try {
      await axios.post(`${API}/contact`, {
        name: user.name || user.email,
        email: user.email,
        phone: user.phone || '',
        subject: `${vehicle.year} ${vehicle.brand} ${vehicle.model} hakkında`,
        message: messageText
      });
      
      toast.success(t('messages.messageSent'));
      setMessageText('');
      setShowMessageDialog(false);
    } catch (error) {
      console.error('Error sending message:', error);
      toast.error(t('messages.messageFailedRetry'));
    }
  };

  const calculateFinance = () => {
    const principal = financeCalc.price - financeCalc.downPayment;
    const monthlyRate = financeCalc.interestRate / 100 / 12;
    const numberOfPayments = financeCalc.loanTerm;
    
    if (monthlyRate === 0) {
      setMonthlyPayment(principal / numberOfPayments);
    } else {
      const monthly = principal * (monthlyRate * Math.pow(1 + monthlyRate, numberOfPayments)) / (Math.pow(1 + monthlyRate, numberOfPayments) - 1);
      setMonthlyPayment(monthly);
    }
  };

  const addToCompare = () => {
    if (!vehicle) return;
    if (hasInCompare(vehicle.id)) {
      removeCompare(vehicle.id);
      toast.success(t('compare.removedFromComparison'));
      return;
    }
    const result = addCompare(vehicle);
    if (result.ok) {
      toast.success(t('vehicleDetail.addedToComparison'));
    } else if (result.reason === 'full') {
      toast.error(t('vehicleDetail.maxThreeVehicles').replace('3', String(maxCompare)));
    } else if (result.reason === 'exists') {
      toast.info(t('vehicleDetail.alreadyInComparison'));
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen pt-20">
        <div className="spinner"></div>
      </div>
    );
  }

  // Sold Vehicle Page
  if (vehicle?.status === 'sold' || vehicle?.isSold) {
    return (
      <>
        <SEOHead 
          title={`${vehicle.year} ${vehicle.brand} ${vehicle.model} | Satıldı | AAA of Miami`}
          description={`Bu araç satılmıştır. Benzer araçlarımızı inceleyin.`}
          type="product"
          canonicalUrl={`/${generateVehicleSlug(vehicle)}`}
        />
        <div className="pt-20 min-h-screen" style={{ background: 'var(--bg-secondary)' }}>
          <div className="max-w-4xl mx-auto px-4 py-12">
            {/* Back Button */}
            <Link to="/inventory" className="inline-flex items-center mb-6" style={{ color: 'var(--text-secondary)' }}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              {t('common.backToInventory')}
            </Link>

            {/* Sold Banner */}
            <div className="rounded-2xl overflow-hidden shadow-xl" style={{ background: 'var(--bg-primary)' }}>
              {/* Vehicle Image with Sold Badge */}
              <div className="relative">
                <img 
                  src={vehicle.images?.[0] || 'https://via.placeholder.com/800x400?text=No+Image'} 
                  alt={`${vehicle.brand} ${vehicle.model}`}
                  className="w-full h-64 md:h-80 object-cover opacity-70"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                <div className="absolute top-4 right-4">
                  <Badge className="bg-red-600 text-white text-lg px-4 py-2 font-bold">
                    SATILDI
                  </Badge>
                </div>
                <div className="absolute bottom-4 left-4 right-4 text-white">
                  <h1 className="text-3xl md:text-4xl font-bold mb-2">
                    {vehicle.year} {vehicle.brand} {vehicle.model}
                  </h1>
                  <p className="text-xl opacity-90">
                    ${vehicle.price?.toLocaleString()}
                  </p>
                </div>
              </div>

              {/* Message */}
              <div className="p-8 text-center">
                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-red-100 flex items-center justify-center">
                  <svg className="w-10 h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h2 className="text-2xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
                  Bu Araç Satılmıştır
                </h2>
                <p className="text-lg mb-8" style={{ color: 'var(--text-secondary)' }}>
                  Maalesef bu araç artık mevcut değil. Ancak envanterimizde benzer birçok seçenek bulunuyor.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/inventory">
                    <Button size="lg" className="w-full sm:w-auto bg-red-600 hover:bg-red-700 text-white">
                      Tüm Araçları Gör
                    </Button>
                  </Link>
                  <Link to="/contact">
                    <Button size="lg" variant="outline" className="w-full sm:w-auto">
                      Bize Ulaşın
                    </Button>
                  </Link>
                </div>
              </div>
            </div>

            {/* Similar Vehicles Section */}
            {similarVehicles.length > 0 && (
              <div className="mt-12">
                <h3 className="text-2xl font-bold mb-6" style={{ color: 'var(--text-primary)' }}>
                  Benzer Araçlar
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {similarVehicles.map(v => (
                    <Link 
                      key={v.id} 
                      to={`/${generateVehicleSlug(v)}`}
                      className="group rounded-xl overflow-hidden shadow-lg transition-transform hover:scale-[1.02]"
                      style={{ background: 'var(--bg-primary)' }}
                    >
                      <div className="relative h-48">
                        <img 
                          src={v.images?.[0] || 'https://via.placeholder.com/400x200'} 
                          alt={`${v.brand} ${v.model}`}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute bottom-2 right-2">
                          <Badge className="bg-green-600 text-white">Mevcut</Badge>
                        </div>
                      </div>
                      <div className="p-4">
                        <h4 className="font-bold text-lg" style={{ color: 'var(--text-primary)' }}>
                          {v.year} {v.brand} {v.model}
                        </h4>
                        <p className="text-red-600 font-bold text-xl mt-1">
                          ${v.price?.toLocaleString()}
                        </p>
                        <p className="text-sm mt-1" style={{ color: 'var(--text-secondary)' }}>
                          {v.mileage?.toLocaleString()} mil
                        </p>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </>
    );
  }

  if (!vehicle) {
    return (
      <div className="flex flex-col justify-center items-center min-h-screen pt-20">
        <p className="text-xl text-gray-600 mb-4">{t('common.vehicleNotFound')}</p>
        <Link to="/inventory">
          <Button>{t('common.backToInventoryBtn')}</Button>
        </Link>
      </div>
    );
  }

  return (
    <>
      <SEOHead 
        title={`${vehicle.year} ${vehicle.brand} ${vehicle.model} | AAA of Miami`}
        description={`${vehicle.year} ${vehicle.brand} ${vehicle.model} for sale at AAA of Miami. ${vehicle.mileage?.toLocaleString()} miles. Price: $${vehicle.price?.toLocaleString()}. ${vehicle.description?.substring(0, 150)}...`}
        type="product"
        image={vehicle.images?.[0]}
        data={vehicle}
        canonicalUrl={`/vehicle/${generateVehicleSlug(vehicle)}`}
      />
      <div className="pt-20 min-h-screen" style={{ background: 'var(--bg-secondary)' }}>
      <div className="max-w-7xl mx-auto px-4 py-12">
        {/* Back Button */}
        <Link to="/inventory" className="inline-flex items-center mb-6" style={{ color: 'var(--text-secondary)' }} data-testid="back-to-inventory">
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t('common.backToInventory')}
        </Link>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Image Gallery */}
          <div>
            {vehicle.images && vehicle.images.length > 0 ? (
              <Carousel className="w-full" data-testid="vehicle-image-carousel">
                <CarouselContent>
                  {vehicle.images.map((image, index) => (
                    <CarouselItem key={index}>
                      <img
                        src={image}
                        alt={`${vehicle.brand} ${vehicle.model} - Image ${index + 1}`}
                        className="w-full h-[300px] md:h-[500px] object-contain bg-gray-100 rounded-lg cursor-zoom-in hover:opacity-90 transition-opacity"
                        loading={index === 0 ? "eager" : "lazy"}
                        data-testid={`vehicle-image-${index}`}
                        onClick={() => openLightbox(index)}
                      />
                    </CarouselItem>
                  ))}
                </CarouselContent>
                <CarouselPrevious />
                <CarouselNext />
              </Carousel>
            ) : (
              <img
                src="https://via.placeholder.com/800x600?text=No+Image"
                alt={`${vehicle.brand} ${vehicle.model}`}
                className="w-full h-[500px] object-cover rounded-lg"
                data-testid="vehicle-placeholder-image"
              />
            )}
            
            {/* Thumbnail Gallery */}
            {vehicle.images && vehicle.images.length > 1 && (
              <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
                {vehicle.images.slice(0, 6).map((image, index) => (
                  <img
                    key={index}
                    src={image}
                    alt={`Thumbnail ${index + 1}`}
                    className="w-16 h-16 md:w-20 md:h-20 object-cover rounded cursor-pointer hover:opacity-80 transition-opacity flex-shrink-0"
                    onClick={() => openLightbox(index)}
                  />
                ))}
                {vehicle.images.length > 6 && (
                  <div 
                    className="w-16 h-16 md:w-20 md:h-20 rounded bg-gray-200 flex items-center justify-center cursor-pointer hover:bg-gray-300 transition-colors flex-shrink-0"
                    onClick={() => openLightbox(6)}
                  >
                    <span className="text-gray-600 font-medium">+{vehicle.images.length - 6}</span>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Vehicle Details */}
          <div>
            <div className="rounded-lg p-8 shadow-lg" style={{ background: 'var(--bg-primary)' }}>
              {vehicle.is_featured && (
                <Badge className="mb-4 bg-gradient-to-r from-yellow-400 to-yellow-600 text-gray-900" data-testid="featured-badge">
                  {t('vehicle.featured')}
                </Badge>
              )}
              
              <h1 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Playfair Display', color: 'var(--text-primary)' }} data-testid="vehicle-title">
                {vehicle.brand} {vehicle.model}
              </h1>
              
              <div className="text-4xl font-bold gold-accent mb-6" data-testid="vehicle-price">
                ${vehicle.price?.toLocaleString()}
              </div>

              {/* Key Specs */}
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="border-l-4 pl-4" style={{ borderColor: 'var(--accent-gold)' }}>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{t('vehicle.year')}</p>
                  <p className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }} data-testid="vehicle-year">{vehicle.year}</p>
                </div>
                <div className="border-l-4 pl-4" style={{ borderColor: 'var(--accent-gold)' }}>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{t('vehicle.mileage')}</p>
                  <p className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }} data-testid="vehicle-mileage">{vehicle.mileage.toLocaleString()} miles</p>
                </div>
                <div className="border-l-4 pl-4" style={{ borderColor: 'var(--accent-gold)' }}>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{t('vehicle.transmission')}</p>
                  <p className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }} data-testid="vehicle-transmission">{vehicle.transmission}</p>
                </div>
                <div className="border-l-4 pl-4" style={{ borderColor: 'var(--accent-gold)' }}>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{t('vehicle.fuelType')}</p>
                  <p className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }} data-testid="vehicle-fuel-type">{vehicle.fuel_type}</p>
                </div>
                <div className="border-l-4 pl-4" style={{ borderColor: 'var(--accent-gold)' }}>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{t('vehicle.engine')}</p>
                  <p className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }} data-testid="vehicle-engine">{vehicle.engine}</p>
                </div>
                <div className="border-l-4 pl-4" style={{ borderColor: 'var(--accent-gold)' }}>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{t('vehicle.color')}</p>
                  <p className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }} data-testid="vehicle-color">{vehicle.color}</p>
                </div>
                {vehicle.vin && (
                  <div className="border-l-4 pl-4" style={{ borderColor: 'var(--accent-gold)' }}>
                    <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{t('vehicle.vin')}</p>
                    <p className="text-lg font-semibold font-mono" style={{ color: 'var(--text-primary)' }} data-testid="vehicle-vin">{vehicle.vin}</p>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="space-y-3">
                {/* WhatsApp Button */}
                <Button
                  className="w-full bg-green-600 hover:bg-green-700 text-white"
                  size="lg"
                  onClick={() => {
                    const message = `${t('vehicle.whatsAppMessage')} ${vehicle.brand} ${vehicle.model} ${vehicle.year} - $${vehicle.price.toLocaleString()}`;
                    const whatsappUrl = `https://wa.me/13054578831?text=${encodeURIComponent(message)}`;
                    window.open(whatsappUrl, '_blank');
                  }}
                  data-testid="whatsapp-btn"
                >
                  <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                  </svg>
                  {t('vehicle.contactWhatsApp')}
                </Button>

                {/* WhatsApp Share Button */}
                <Button
                  variant="outline"
                  className="w-full border-2 border-green-500 text-green-600 hover:bg-green-50"
                  size="lg"
                  onClick={() => {
                    const url = `${window.location.origin}/${generateVehicleSlug(vehicle)}`;
                    const text = `${vehicle.year} ${vehicle.brand} ${vehicle.model} - $${vehicle.price.toLocaleString()}\n${vehicle.mileage.toLocaleString()} miles\n${url}`;
                    window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank');
                  }}
                  data-testid="whatsapp-share-btn"
                >
                  <Share2 className="w-5 h-5 mr-2" />
                  {t('vehicle.shareWhatsApp')}
                </Button>

                {/* Message Button */}
                <Dialog open={showMessageDialog} onOpenChange={setShowMessageDialog}>
                  <DialogTrigger asChild>
                    <Button 
                      variant="outline" 
                      className="w-full border-2" 
                      size="lg"
                      style={{ borderColor: 'var(--accent)', color: 'var(--accent)' }}
                      data-testid="send-message-btn"
                    >
                      <MessageSquare className="w-5 h-5 mr-2" />
                      {t('messages.messageSeller')}
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{t('messages.sendToSeller')}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <p className="text-sm mb-2" style={{ color: 'var(--text-secondary)' }}>
                          {vehicle.year} {vehicle.brand} {vehicle.model} hakkında
                        </p>
                        <Textarea
                          placeholder={t('messages.writeYourMessage')}
                          value={messageText}
                          onChange={(e) => setMessageText(e.target.value)}
                          rows={5}
                        />
                      </div>
                      <Button onClick={handleSendMessage} className="w-full">
                        <MessageSquare className="w-4 h-4 mr-2" />
                        {t('messages.send')}
                      </Button>
                      {!user && (
                        <p className="text-sm text-center" style={{ color: 'var(--text-secondary)' }}>
                          {t('messages.loginRequired')}
                        </p>
                      )}
                    </div>
                  </DialogContent>
                </Dialog>

                <Dialog>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="w-full border-2 border-gray-900" size="lg" data-testid="request-quote-btn">
                      <DollarSign className="w-5 h-5 mr-2" />
                      {t('vehicle.requestQuote')}
                    </Button>
                  </DialogTrigger>
                  <DialogContent data-testid="quote-dialog">
                    <DialogHeader>
                      <DialogTitle>{t('quote.title')}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleQuoteSubmit} className="space-y-4">
                      <Input
                        placeholder={t('contact.name')}
                        value={quoteForm.name}
                        onChange={(e) => setQuoteForm({...quoteForm, name: e.target.value})}
                        required
                        data-testid="quote-name"
                      />
                      <Input
                        type="email"
                        placeholder={t('contact.email')}
                        value={quoteForm.email}
                        onChange={(e) => setQuoteForm({...quoteForm, email: e.target.value})}
                        required
                        data-testid="quote-email"
                      />
                      <Input
                        placeholder={t('contact.phone')}
                        value={quoteForm.phone}
                        onChange={(e) => setQuoteForm({...quoteForm, phone: e.target.value})}
                        required
                        data-testid="quote-phone"
                      />
                      <Input
                        type="number"
                        placeholder={t('quote.offeredPrice')}
                        value={quoteForm.offered_price}
                        onChange={(e) => setQuoteForm({...quoteForm, offered_price: e.target.value})}
                        data-testid="quote-offered-price"
                      />
                      <Input
                        placeholder={t('contact.message')}
                        value={quoteForm.message}
                        onChange={(e) => setQuoteForm({...quoteForm, message: e.target.value})}
                        data-testid="quote-message"
                      />
                      <Button type="submit" className="w-full" data-testid="submit-quote">{t('quote.submit')}</Button>
                    </form>
                  </DialogContent>
                </Dialog>

                <div className="flex gap-3">
                  <Button
                    onClick={toggleFavorite}
                    variant="outline"
                    className="flex-1"
                    data-testid="favorite-btn"
                  >
                    <Heart className={`w-5 h-5 mr-2 ${isFavorite ? 'fill-red-500 text-red-500' : ''}`} />
                    {isFavorite ? t('vehicle.removeFromFavorites') : t('vehicle.addToFavorites')}
                  </Button>
                  <Button
                    onClick={addToCompare}
                    variant="outline"
                    className="flex-1"
                    data-testid="compare-btn"
                  >
                    {t('vehicle.compare')}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Description and Features */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 mt-12">
          <div className="lg:col-span-2">
            <div className="rounded-lg p-8 shadow-lg" style={{ background: 'var(--bg-primary)' }}>
              <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: 'Playfair Display', color: 'var(--text-primary)' }}>{t('vehicle.description')}</h2>
              <p className="leading-relaxed" style={{ color: 'var(--text-secondary)' }} data-testid="vehicle-description">{vehicle.description}</p>

              {vehicle.features && vehicle.features.length > 0 && (
                <div className="mt-8">
                  <h3 className="text-xl font-bold mb-4" style={{ fontFamily: 'Playfair Display', color: 'var(--text-primary)' }}>{t('vehicle.features')}</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="vehicle-features">
                    {vehicle.features.map((feature, index) => (
                      <div key={index} className="flex items-center space-x-2">
                        <div className="w-2 h-2 rounded-full" style={{ background: 'var(--accent-gold)' }}></div>
                        <span style={{ color: 'var(--text-secondary)' }}>{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Finance Calculator */}
          <div>
            <div className="rounded-lg p-6 shadow-lg" style={{ background: 'var(--bg-primary)' }}>
              <h3 className="text-xl font-bold mb-4 flex items-center" style={{ fontFamily: 'Playfair Display', color: 'var(--text-primary)' }}>
                <Calculator className="w-5 h-5 mr-2 gold-accent" />
                {t('finance.calculator')}
              </h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>{t('finance.vehiclePrice')}</label>
                  <Input
                    type="number"
                    value={financeCalc.price}
                    onChange={(e) => setFinanceCalc({...financeCalc, price: Number(e.target.value)})}
                    data-testid="finance-price"
                    style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>{t('finance.downPayment')}</label>
                  <Input
                    type="number"
                    value={financeCalc.downPayment}
                    onChange={(e) => setFinanceCalc({...financeCalc, downPayment: Number(e.target.value)})}
                    data-testid="finance-down-payment"
                    style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>{t('finance.interestRate')}</label>
                  <Input
                    type="number"
                    step="0.1"
                    value={financeCalc.interestRate}
                    onChange={(e) => setFinanceCalc({...financeCalc, interestRate: Number(e.target.value)})}
                    data-testid="finance-interest-rate"
                    style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-primary)' }}>{t('finance.loanTerm')}</label>
                  <Input
                    type="number"
                    value={financeCalc.loanTerm}
                    onChange={(e) => setFinanceCalc({...financeCalc, loanTerm: Number(e.target.value)})}
                    data-testid="finance-loan-term"
                    style={{ background: 'var(--bg-secondary)', color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}
                  />
                </div>
                <Button onClick={calculateFinance} className="w-full" data-testid="calculate-finance-btn">{t('finance.calculate')}</Button>
                {monthlyPayment > 0 && (
                  <div className="p-4 rounded-lg border-2" style={{ background: 'var(--bg-secondary)', borderColor: 'var(--accent-gold)' }} data-testid="monthly-payment-result">
                    <p className="text-sm mb-1" style={{ color: 'var(--text-secondary)' }}>{t('finance.monthlyPayment')}</p>
                    <p className="text-2xl font-bold gold-accent">${monthlyPayment.toFixed(2)}</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Customer Reviews Section */}
        <div className="max-w-7xl mx-auto px-4 py-12 mt-12">
          <div className="rounded-lg p-8 shadow-lg" style={{ background: 'var(--bg-primary)' }}>
            <div className="flex justify-between items-center mb-8">
              <h2 className="text-3xl font-bold" style={{ fontFamily: 'Playfair Display', color: 'var(--text-primary)' }}>
                {t('reviews.title')}
              </h2>
              <Button onClick={() => setShowReviewForm(!showReviewForm)} className="bg-blue-600 hover:bg-blue-700">
                {t('reviews.writeReview')}
              </Button>
            </div>

            {/* Review Form */}
            {showReviewForm && (
              <form onSubmit={handleReviewSubmit} className="mb-8 p-6 rounded-lg" style={{ background: 'var(--bg-secondary)' }}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <Input
                    placeholder={t('reviews.yourName')}
                    value={reviewForm.customer_name}
                    onChange={(e) => setReviewForm({...reviewForm, customer_name: e.target.value})}
                    required
                    style={{ background: 'var(--bg-tertiary)', color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}
                  />
                  <Input
                    type="email"
                    placeholder={t('reviews.yourEmail')}
                    value={reviewForm.customer_email}
                    onChange={(e) => setReviewForm({...reviewForm, customer_email: e.target.value})}
                    required
                    style={{ background: 'var(--bg-tertiary)', color: 'var(--text-primary)', borderColor: 'var(--border-color)' }}
                  />
                </div>
                
                <div className="mb-4">
                  <label className="block mb-2 font-medium" style={{ color: 'var(--text-primary)' }}>{t('reviews.yourRating')}</label>
                  <div className="flex space-x-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setReviewForm({...reviewForm, rating: star})}
                        className="text-3xl focus:outline-none transition-colors"
                        style={{ color: star <= reviewForm.rating ? '#fbbf24' : '#9ca3af' }}
                      >
                        ★
                      </button>
                    ))}
                  </div>
                </div>

                <textarea
                  placeholder={t('reviews.yourReview')}
                  value={reviewForm.comment}
                  onChange={(e) => setReviewForm({...reviewForm, comment: e.target.value})}
                  required
                  rows={4}
                  className="w-full p-3 rounded-lg mb-4"
                  style={{ background: 'var(--bg-tertiary)', color: 'var(--text-primary)', borderColor: 'var(--border-color)', border: '1px solid' }}
                />

                <div className="flex space-x-2">
                  <Button type="submit" className="bg-blue-600 hover:bg-blue-700">
                    {t('reviews.submitReview')}
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setShowReviewForm(false)}>
                    {t('common.cancel')}
                  </Button>
                </div>
              </form>
            )}

            {/* Reviews List */}
            {reviews.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-lg mb-2" style={{ color: 'var(--text-secondary)' }}>{t('reviews.noReviews')}</p>
                <p style={{ color: 'var(--text-tertiary)' }}>{t('reviews.beFirst')}</p>
              </div>
            ) : (
              <div className="space-y-6">
                {reviews.map((review) => (
                  <div key={review.id} className="p-6 rounded-lg" style={{ background: 'var(--bg-secondary)', borderLeft: '4px solid var(--accent-gold)' }}>
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h4 className="font-bold text-lg" style={{ color: 'var(--text-primary)' }}>{review.customer_name}</h4>
                        <div className="flex items-center space-x-2 mt-1">
                          <div className="flex">
                            {[1, 2, 3, 4, 5].map((star) => (
                              <span key={star} style={{ color: star <= review.rating ? '#fbbf24' : '#9ca3af' }}>★</span>
                            ))}
                          </div>
                          <span className="text-sm" style={{ color: 'var(--text-tertiary)' }}>
                            ({review.rating} {t('reviews.stars')})
                          </span>
                        </div>
                      </div>
                      <span className="text-sm" style={{ color: 'var(--text-tertiary)' }}>
                        {new Date(review.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <p style={{ color: 'var(--text-secondary)' }}>{review.comment}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
    
    {/* Lightbox Modal */}
    {lightboxOpen && vehicle?.images && (
      <div 
        className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
        onClick={closeLightbox}
        data-testid="lightbox-modal"
      >
        {/* Close button */}
        <button
          className="absolute top-4 right-4 text-white hover:text-gray-300 z-10 p-2"
          onClick={closeLightbox}
          data-testid="lightbox-close"
        >
          <X className="w-8 h-8" />
        </button>

        {/* Image counter */}
        <div className="absolute top-4 left-4 text-white text-lg font-medium">
          {lightboxIndex + 1} / {vehicle.images.length}
        </div>

        {/* Previous button */}
        <button
          className="absolute left-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 p-2 bg-black/50 rounded-full"
          onClick={(e) => { e.stopPropagation(); goToPrevImage(); }}
          data-testid="lightbox-prev"
        >
          <ChevronLeft className="w-8 h-8" />
        </button>

        {/* Main image */}
        <img
          src={vehicle.images[lightboxIndex]}
          alt={`${vehicle.brand} ${vehicle.model} - Image ${lightboxIndex + 1}`}
          className="max-h-[90vh] max-w-[90vw] object-contain"
          onClick={(e) => e.stopPropagation()}
          data-testid="lightbox-image"
        />

        {/* Next button */}
        <button
          className="absolute right-4 top-1/2 -translate-y-1/2 text-white hover:text-gray-300 p-2 bg-black/50 rounded-full"
          onClick={(e) => { e.stopPropagation(); goToNextImage(); }}
          data-testid="lightbox-next"
        >
          <ChevronRight className="w-8 h-8" />
        </button>

        {/* Thumbnail strip */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 max-w-[90vw] overflow-x-auto p-2">
          {vehicle.images.map((image, index) => (
            <img
              key={index}
              src={image}
              alt={`Thumbnail ${index + 1}`}
              className={`w-16 h-12 object-cover rounded cursor-pointer transition-all ${
                index === lightboxIndex 
                  ? 'ring-2 ring-white opacity-100' 
                  : 'opacity-60 hover:opacity-100'
              }`}
              onClick={(e) => { e.stopPropagation(); setLightboxIndex(index); }}
            />
          ))}
        </div>
      </div>
    )}
    </>
  );
};

export default VehicleDetailPage;
